import React, { useEffect, useRef, useState } from "react";
import { PUT, alert } from "../../components/CustomComponent";

const API = "v1/cmru/class-sections";
const HARD_MAX = 999.99;

/**
 * ช่องตัวเลขที่แก้ได้ในตาราง ใช้งานเหมือนช่องชื่อเล่น (NicknameCell):
 * คลิก -> พิมพ์ตัวเลข -> Enter หรือคลิกที่อื่น = บันทึกทันที, Esc = ยกเลิก, ลบจนว่าง = ล้างค่า (null)
 * validate(next) คืนข้อความเตือนถ้าค่าใช้ไม่ได้ · save(next) ยิง API แล้วคืน result · nextSelector = ช่องที่จะโฟกัสต่อหลังกด Enter
 */
function InlineNumber({ saved, validate, save, onSaved, failTitle, nextSelector, inputLabel, renderButton }) {
  const savedText = saved === null ? "" : String(saved);
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(savedText);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);
  // กัน blur ที่ตามมาหลัง Enter/Esc บันทึกซ้ำอีกรอบ
  const skipBlurRef = useRef(false);

  useEffect(() => {
    if (!isEditing) setValue(savedText);
  }, [savedText, isEditing]);

  useEffect(() => {
    if (isEditing) {
      // ช่องกรอกถูกถอดออกหลัง Enter/Esc โดยไม่มี blur ตามมา ธงจึงค้างเป็น true ต้องล้างก่อนเริ่มแก้รอบใหม่
      skipBlurRef.current = false;
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const commit = async (goNext) => {
    const text = value.trim();
    // ปัดเหลือทศนิยม 2 ตำแหน่งให้ตรงกับที่ฐานข้อมูลเก็บ
    const next = text === "" ? null : Math.round(Number(text) * 100) / 100;
    setIsEditing(false);

    const invalid = next !== null && (!Number.isFinite(next) || next < 0 || next > HARD_MAX) ? `กรุณากรอกตัวเลข 0 - ${HARD_MAX}` : validate?.(next);
    if (invalid) {
      setValue(savedText);
      alert("", "warning", "แจ้งเตือน", invalid);
      return;
    }

    if (next !== saved) {
      setIsSaving(true);
      const result = await save(next);
      setIsSaving(false);

      if (result?.status === true) {
        onSaved(next);
      } else {
        setValue(savedText);
        alert("", "warning", failTitle, result?.message || "กรุณาลองใหม่อีกครั้ง");
        return;
      }
    }

    if (goNext && nextSelector) document.querySelector(nextSelector)?.click();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      skipBlurRef.current = true;
      commit(true);
    } else if (event.key === "Escape") {
      skipBlurRef.current = true;
      setValue(savedText);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="number"
        inputMode="decimal"
        min="0"
        max={HARD_MAX}
        step="0.5"
        className="cmru-nick-input cmru-bonus-input"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (skipBlurRef.current) skipBlurRef.current = false;
          else commit(false);
        }}
        aria-label={inputLabel}
      />
    );
  }

  return renderButton({ isSaving, onClick: () => setIsEditing(true) });
}

/**
 * คะแนนโบนัสของนักศึกษาในรายวิชานี้ (เก็บที่การลงทะเบียน) ใส่ได้ไม่เกิน max ของกลุ่มเรียน (null = ไม่จำกัด)
 * Enter แล้วเลื่อนไปช่องโบนัสของแถวถัดไปให้เลย จะได้ไล่กรอกทั้งห้องได้โดยไม่ต้องจับเมาส์
 */
export default function BonusCell({ sectionUid, enrollment, max, rowIndex, onSaved }) {
  const saved = enrollment.bonus_score ?? null;

  return (
    <InlineNumber
      saved={saved}
      validate={(next) => (next !== null && max !== null && max !== undefined && next > max ? `โบนัสใส่ได้ไม่เกิน ${max} (แก้ค่าสูงสุดได้ที่ช่องใต้หัวคอลัมน์ "โบนัส")` : "")}
      // keepEmpty: null มีความหมาย (ลบโบนัส) ปกติ PUT จะตัด key ที่เป็น null ทิ้งก่อนส่ง
      save={(next) => PUT(`${API}/${sectionUid}/students/${enrollment.uid}/bonus`, { bonus_score: next }, null, { keepEmpty: true, silent: true })}
      onSaved={onSaved}
      failTitle="บันทึกโบนัสไม่สำเร็จ"
      nextSelector={`[data-bonus-row="${rowIndex + 1}"]`}
      inputLabel={`โบนัสของ ${enrollment.student?.first_name || ""}`}
      renderButton={({ isSaving, onClick }) => (
        <button
          type="button"
          className={`cmru-nick-btn cmru-bonus-btn ${saved === null ? "is-empty" : ""}`}
          data-bonus-row={rowIndex}
          onClick={onClick}
          disabled={isSaving}
          title={`คลิกเพื่อใส่คะแนนโบนัส${max !== null && max !== undefined ? ` (ไม่เกิน ${max})` : ""} · Enter = บันทึกแล้วไปคนถัดไป · Esc = ยกเลิก · ลบจนว่าง = ลบโบนัส`}
        >
          {isSaving ? <span className="spinner-border spinner-border-sm" /> : saved === null ? <><i className="fa-solid fa-plus" />เพิ่ม</> : <span className={`cmru-bonus-value ${max !== null && max !== undefined && saved >= max ? "is-full" : ""}`}>+{saved}</span>}
        </button>
      )}
    />
  );
}

/**
 * ตัวเลขใต้หัวคอลัมน์ "โบนัส" = คะแนนโบนัสสูงสุดที่ให้ได้ต่อคนของกลุ่มเรียนนี้ แก้ในช่องได้เลย ลบจนว่าง = ไม่จำกัด
 * highest = โบนัสสูงสุดที่ให้ไปแล้วในห้อง ลดเพดานต่ำกว่านี้ไม่ได้
 */
export function BonusMaxCell({ sectionUid, max, highest, onSaved }) {
  const saved = max ?? null;

  return (
    <InlineNumber
      saved={saved}
      validate={(next) => (next !== null && highest > next ? `มีนักศึกษาได้โบนัส ${highest} อยู่แล้ว ค่าสูงสุดต้องไม่ต่ำกว่านี้` : "")}
      save={(next) => PUT(`${API}/${sectionUid}/bonus-max`, { bonus_max: next }, null, { keepEmpty: true, silent: true })}
      onSaved={onSaved}
      failTitle="บันทึกค่าสูงสุดของโบนัสไม่สำเร็จ"
      nextSelector='[data-bonus-row="0"]'
      inputLabel="คะแนนโบนัสสูงสุด"
      renderButton={({ isSaving, onClick }) => (
        <button type="button" className={`cmru-bonus-max ${saved === null ? "is-empty" : ""}`} onClick={onClick} disabled={isSaving} title="คะแนนโบนัสสูงสุดที่ให้ได้ต่อคน · คลิกเพื่อแก้ · ลบจนว่าง = ไม่จำกัด">
          {isSaving ? <span className="spinner-border spinner-border-sm" /> : saved === null ? "ไม่จำกัด" : saved}
        </button>
      )}
    />
  );
}
